/**
 * 🧮 원가분석 창 — 견적서 작성 창 옆에 떠 있는 창 (v2.248.0 · 2026-10-13)
 *
 * 사용자: 「문서작성에서 견적서 작성메뉴에서 작성하다가도 원가분석을 할 수 있으면 좋겠어」 → 제안 ① 「원가분석 창 옆에 띄우기」 선택.
 *   v2.237.0(원가분석 탭에서 견적 창을 띄움)의 반대 방향 — 견적서 작성 창의 [🧮 원가분석] 이 이 창을 왼쪽에 띄운다.
 * 안은 원가분석 탭과 같은 CostSheetEditor(compact) — 매입·인포트렌드 가져오기 · 마진율 · 시나리오 · 🎯 목표 금액 · 자동 저장.
 * 맨 아래 [견적서에 반영 ➡] 이 표의 지금 값(품목·규격·수량·판매 단가·비고 · 원가)을 견적서 품목으로 넘긴다.
 * 층: 견적서 창과 이 창 중 **마지막으로 누른 창이 위**(onFocus) — 이 창이 여는 가져오기 창은 이 창 층 안에서 뜬다.
 */
import { DraggableModal } from '../DraggableModal';
import { toQuoteDraft } from '../../lib/cost-sheet-calc';
import type { QuoteItemInput } from '../../lib/quote-api';
import { CostSheetEditor } from './CostSheetEditor';
import type { CostSheetStore } from './useCostSheets';

export function CostSheetWindow({ store, sheetId, canEdit, zIndex, initialX, initialWidth, onFocus, onApply, onClose }: {
  store: CostSheetStore;
  sheetId: number;
  canEdit: boolean;
  zIndex: number;
  initialX: number;
  initialWidth: number;
  /** 이 창을 눌렀다 — 위로 올린다 */
  onFocus: () => void;
  /** [견적서에 반영 ➡] — 표의 지금 값으로 만든 견적 품목(이름이 빈 줄·수량 0 줄은 빠진다 · skipped) */
  onApply: (items: QuoteItemInput[], skipped: number) => void;
  onClose: () => void;
}) {
  const sheet = store.sheets.find((s) => s.id === sheetId) ?? null;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  const apply = (): void => {
    if (!sheet) return;
    // 줄은 캐시의 최신 값으로(방금 친 글자까지) — 화면 변수는 한 박자 늦을 수 있다
    const latest = { ...sheet, lines: store.currentLines(sheet.id) };
    const { draft, skipped } = toQuoteDraft(latest);
    if (draft.items.length === 0) {
      window.alert('견적서에 넣을 줄이 없습니다 — 품목명과 수량이 있는 줄이 하나는 있어야 합니다.');
      return;
    }
    onApply(draft.items, skipped);
  };

  return (
    <DraggableModal
      onClose={onClose}
      backdrop={false}
      zIndex={zIndex}
      initialWidth={initialWidth}
      initialHeight={vh - 24}
      initialX={initialX}
      initialY={12}
      minWidth={560}
      bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
    >
      <div className="contents" onPointerDownCapture={onFocus}>
        <div className="dm-drag-handle cursor-move select-none px-4 py-2.5 border-b border-slate-200 bg-emerald-50 flex items-center justify-between gap-3">
          <h2 className="ui-section-title min-w-0 truncate">
            🧮 원가분석
            <span className="ml-2 text-xs font-normal text-slate-500">
              — 자동 저장 · 원가분석 탭 목록에도 남습니다 · 머리를 끌어 옮기고 오른쪽 아래 모서리로 크기 조절
            </span>
          </h2>
          <button type="button" onClick={onClose} className="ui-close" title="원가분석 창 닫기 (표는 저장되어 남습니다)">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-50">
          {sheet ? (
            <CostSheetEditor sheet={sheet} store={store} canEdit={canEdit} compact />
          ) : store.query.isPending ? (
            <div className="p-10 text-center text-sm text-slate-400">원가분석표 불러오는 중…</div>
          ) : (
            <div className="p-10 text-center text-sm text-red-600">원가분석표를 찾지 못했습니다 (지워졌을 수 있습니다). 창을 닫고 [🧮 원가분석] 을 다시 누르세요.</div>
          )}
        </div>
        {sheet && canEdit && (
          <div className="px-4 py-2.5 border-t border-slate-200 bg-white flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-slate-500">
              표의 품목·규격·수량·<b>판매 단가</b>·비고를 견적서 품목으로 바꿉니다(원가도 따라가 견적서 줄마다 마진이 보입니다).
            </span>
            <button type="button" onClick={apply}
              className="btn btn-success-solid">
              견적서에 반영 ➡
            </button>
          </div>
        )}
      </div>
    </DraggableModal>
  );
}
